export type SupportedCurrency = 'BRL' | 'USD';

/** Format a number as Brazilian Real (BRL). */
export function formatBRL(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  }).format(amount);
}

/** Format a number as US Dollar (USD). */
export function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(amount);
}

export function formatPrice(amount: number, currency: SupportedCurrency = 'BRL'): string {
  return currency === 'BRL' ? formatBRL(amount) : formatUSD(amount);
}

/** Build a Stripe price lookup_key for a product + currency. */
export function getLookupKey(productId: string, currency: SupportedCurrency): string {
  return `${productId}_${currency.toLowerCase()}`;
}

// All IANA timezone identifiers used in Brazil.
const BRAZIL_TIMEZONES = new Set([
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Maceio',
  'America/Bahia',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Eirunepe',
  'America/Rio_Branco',
  'America/Araguaina',
  'America/Noronha',
]);

/**
 * Detect the user's preferred currency, no external API, no CORS, instant.
 * 1. Browser language (pt-BR / pt → BRL).
 * 2. Browser timezone (any Brazilian IANA zone → BRL).
 * 3. Fallback: USD.
 */
export function detectUserCurrency(): SupportedCurrency {
  const lang = navigator.language?.toLowerCase() ?? '';
  if (lang === 'pt-br' || lang.startsWith('pt')) return 'BRL';

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (BRAZIL_TIMEZONES.has(tz)) return 'BRL';
  } catch {
    // Intl not available, fall through
  }

  return 'USD';
}
